/**
 * Native in-app purchase bridge (web side).
 *
 * SUSU Closet runs inside a thin React Native WebView shell on iOS and Android.
 * Store purchases have to happen natively — Apple and Google do not allow paying
 * for digital credits any other way — so the web app asks the shell to run the
 * purchase and then asks OUR backend what the user actually owns.
 *
 * The important rule: the web app never decides that credits were granted. It
 * used to flip straight to "+20 credits added" the moment the shell reported a
 * successful purchase, without telling the backend anything, so the number on
 * screen was fiction and the real balance never moved. Now every purchase ends
 * with `syncPurchases()`, and the balance shown is the one the backend returns
 * after verifying with RevenueCat.
 */

import { BACKEND_URL } from './config';

/** Which app's store product ids to use when reading the backend catalogue. */
export const APP_KEY = 'susu';

/**
 * What the native shell told us it can do. Older shells that predate the
 * capability script only expose `window.ReactNativeWebView`, so fall back to
 * assuming purchases work there.
 */
export function getNativeCapabilities() {
  const declared = typeof window !== 'undefined' ? window.__NATIVE_APP__ : null;
  if (declared) return declared;

  const legacyNative = typeof window !== 'undefined' && window.ReactNativeWebView !== undefined;
  return legacyNative
    ? { platform: 'unknown', iap: { available: true, store: 'unknown' } }
    : null;
}

export function isNativeApp() {
  return getNativeCapabilities() !== null;
}

export function isIapAvailable() {
  const caps = getNativeCapabilities();
  return !!(caps && caps.iap && caps.iap.available);
}

/** Ask the native shell to start a store purchase. */
export function startNativePurchase(storeProductId) {
  window.ReactNativeWebView.postMessage(
    JSON.stringify({ type: 'INITIATE_IAP', payload: { productId: storeProductId } })
  );
}

/** Ask the native shell to restore previous purchases. */
export function requestNativeRestore() {
  window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'RESTORE_PURCHASES' }));
}

/**
 * Subscribe to messages from the native shell.
 *
 * The shell delivers messages through `window.postMessage`, which lands on
 * `window` on iOS and (historically) on `document` on Android, so both are bound.
 *
 * @param {(type: string, payload: object) => void} onMessage
 * @returns {() => void} unsubscribe
 */
export function onNativeMessage(onMessage) {
  const handler = (event) => {
    let data;
    try {
      data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
    } catch (e) {
      return;
    }
    if (data && typeof data.type === 'string') {
      onMessage(data.type, data.payload || {});
    }
  };

  window.addEventListener('message', handler);
  document.addEventListener('message', handler);
  return () => {
    window.removeEventListener('message', handler);
    document.removeEventListener('message', handler);
  };
}

/**
 * Reconcile this user's balance with the store, via the backend.
 *
 * The backend looks the user up in RevenueCat using the id in the auth token and
 * grants anything not yet granted, so this request carries no product, price or
 * amount — there is nothing in it to tamper with.
 *
 * RevenueCat can take a moment to record a fresh transaction, so a sync that
 * finds nothing is retried a few times with backoff before giving up. The grant
 * itself is idempotent, so retrying can never double-credit.
 *
 * @returns {Promise<{creditsGranted: number, creditBalance: number, found: boolean}>}
 */
export async function syncPurchases({ attempts = 4, delayMs = 1200 } = {}) {
  let lastError = null;

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      const res = await fetch(`${BACKEND_URL}/revenuecat/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: '{}',
      });

      if (res.ok) {
        const data = await res.json();
        // `found` false means RevenueCat has no record for this user yet — the
        // purchase is still posting. Worth another look.
        if (data.found || data.creditsGranted > 0) return data;
        lastError = new Error('The store has not confirmed this purchase yet.');
      } else if (res.status === 401) {
        throw new Error('Please sign in again to receive your credits.');
      } else {
        const body = await res.json().catch(() => ({}));
        lastError = new Error(body.message || `Verification failed (${res.status})`);
      }
    } catch (err) {
      // A thrown auth error is final; anything else is worth retrying.
      if (/sign in again/.test(err.message)) throw err;
      lastError = err;
    }

    if (attempt < attempts - 1) {
      await new Promise((resolve) => setTimeout(resolve, delayMs * (attempt + 1)));
    }
  }

  throw lastError || new Error('Could not confirm your purchase.');
}

/**
 * Store product id for a catalogue entry, preferring what the backend declared.
 * Falling back to the conventional shape keeps older cached catalogues working.
 */
export function storeProductId(product) {
  if (product && product.storeProductIds && product.storeProductIds[APP_KEY]) {
    return product.storeProductIds[APP_KEY];
  }
  const kind = product && product.interval ? 'subscription' : 'credits';
  return `${APP_KEY}.${kind}.${product ? product.id : ''}`;
}

/** Where the user manages a store subscription. */
export function storeManageUrl() {
  const caps = getNativeCapabilities();
  const store = caps && caps.iap ? caps.iap.store : null;
  if (store === 'play_store') return 'https://play.google.com/store/account/subscriptions';
  return 'https://apps.apple.com/account/subscriptions';
}
